// frontend/src/components/ConversationSidebar.tsx
import React, { useState } from 'react';
import { ConversationPreview } from '../types';
import { deleteConversation, updateConversationTitle } from '../api';

interface ConversationSidebarProps {
    conversations: ConversationPreview[];
    loading: boolean;
    onSelect: (id: string) => void;
    onNew: () => void;
    currentId?: string;
    onConversationUpdated: () => void;
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
    conversations,
    loading,
    onSelect,
    onNew,
    currentId,
    onConversationUpdated
}) => {
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingTitle, setEditingTitle] = useState('');
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [updateError, setUpdateError] = useState<string | null>(null);

    const handleEdit = (conv: ConversationPreview, e: React.SyntheticEvent) => {
        e.stopPropagation();
        setEditingId(conv._id);
        setEditingTitle(conv.title || '');
        setUpdateError(null);
    };

    const handleSaveTitle = async (id: string, e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (editingTitle.trim()) {
            try {
                setIsUpdating(true);
                setUpdateError(null);
                const updatedConversation = await updateConversationTitle(id, editingTitle);
                if (updatedConversation) {
                    onConversationUpdated();
                } else {
                    setUpdateError('Échec de la mise à jour du titre : réponse invalide');
                }
            } catch (error) {
                setUpdateError('Échec de la mise à jour du titre');
                console.error('Failed to update title:', error);
            } finally {
                setIsUpdating(false);
                setEditingId(null);
            }
        } else {
            setEditingId(null);
        }
    };

    const handleCancelEdit = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        setEditingId(null);
        setUpdateError(null);
    };

    const handleDelete = async (id: string, e: React.SyntheticEvent) => {
        e.stopPropagation();
        
        if (window.confirm('Voulez-vous vraiment supprimer cette conversation ?')) {
            try {
                setIsUpdating(true);
                setUpdateError(null);
                const success = await deleteConversation(id);
                if (success) {
                    onConversationUpdated();
                    if (id === currentId) {
                        onNew();
                    }
                } else {
                    setUpdateError('Échec de la suppression de la conversation : réponse invalide');
                }
            } catch (error) {
                setUpdateError('Échec de la suppression de la conversation');
                console.error('Failed to delete conversation:', error);
            } finally {
                setIsUpdating(false);
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, action: (e: React.SyntheticEvent) => void) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            action(e);
        }
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString(undefined, { 
            day: '2-digit', 
            month: 'short', 
            year: dateStr.includes('2022') || dateStr.includes('2023') ? 'numeric' : undefined
        });
    };

    return (
        <div className="conversation-sidebar">
            <button className="new-conversation-button" onClick={onNew} disabled={isUpdating}>
                Nouvelle conversation
            </button>
            
            {updateError && <div className="error">{updateError}</div>}
            
            <div className="conversations-list">
                {loading ? (
                    <div className="loading-conversations">Chargement...</div>
                ) : !conversations || conversations.length === 0 ? (
                    <div className="no-conversations">
                        Aucune conversation existante
                    </div>
                ) : (
                    conversations.map(conv => (
                        <div 
                            key={conv._id}
                            className={`conversation-item ${currentId === conv._id ? 'active' : ''}`}
                            onClick={() => !isUpdating && onSelect(conv._id)}
                        >
                            {editingId === conv._id ? (
                                <form onSubmit={(e) => handleSaveTitle(conv._id, e)}>
                                    <input
                                        className="conversation-rename-input"
                                        value={editingTitle}
                                        onChange={(e) => setEditingTitle(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        autoFocus
                                        onBlur={(e) => handleSaveTitle(conv._id, e)}
                                        disabled={isUpdating}
                                    />
                                    <button
                                        type="button"
                                        className="conversation-tool-button"
                                        onClick={handleCancelEdit}
                                        onKeyDown={(e) => handleKeyDown(e, handleCancelEdit)}
                                        aria-label="Annuler l'édition"
                                    >
                                        ✕
                                    </button>
                                </form>
                            ) : (
                                <div className="conversation-title">
                                    {conv.title || "Conversation sans titre"}
                                </div>
                            )}
                            
                            <div className="conversation-date">
                                {formatDate(conv.updatedAt)}
                            </div>
                            
                            <div className="conversation-model">
                                {conv.modelName || 'Modèle inconnu'}
                            </div>
                            
                            {!editingId && (
                                <div className="conversation-tools">
                                    <button
                                        className="conversation-tool-button"
                                        onClick={(e) => handleEdit(conv, e)}
                                        onKeyDown={(e) => handleKeyDown(e, (event) => handleEdit(conv, event))}
                                        aria-label="Renommer la conversation"
                                        disabled={isUpdating}
                                    >
                                        ✏️
                                    </button>
                                    <button
                                        className="conversation-tool-button"
                                        onClick={(e) => handleDelete(conv._id, e)}
                                        onKeyDown={(e) => handleKeyDown(e, (event) => handleDelete(conv._id, event))}
                                        aria-label="Supprimer la conversation"
                                        disabled={isUpdating}
                                    >
                                        🗑️
                                    </button>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default React.memo(ConversationSidebar);